import * as React from "react";
import { RouteComponentProps } from "@reach/router";
import { FormikProps, Field } from "formik";
import { cx, css } from "@emotion/css";
import { navigate } from "../support/hashRouting";
import { FormValues } from "../support/FormValues";
import Breadcrumbs from "../components/Breadcrumbs";
import { ClassName } from "../support/className";
import bootstrapStyles from "../css/bootstrap.module.css";
import formGroupStyles from "../css/formgroup.module.css";
import { StepHeader } from "../components/StepHeader";
import { GeneralButton } from "../components/GeneralButton";
import FieldRadioList from "../components/FieldRadioList";
import { ComplicateOption } from "../support/InterfaceComplicateOption";
import { TransferInstructionsEnum } from "../support/TransferInstructionsEnum";
import { FieldNameEnum } from "../support/FieldNameEnum";
import FieldTextPercentage from "../components/FieldTextPercentage";
import { PfTooltip } from "../components/PfTootip";
import { useScrollTop } from "../customHooks/scrollTop";
import { RothPortionEnum } from "../support/RothPortionEnum";
import FieldTextMoney from "../components/FieldTextMoney";

interface TransferInstructionsProps extends RouteComponentProps {
  formikBag: FormikProps<FormValues>;
}

const stepHeaderText =
  "Provide a few more details about how you want to transfer the account.";
const stepNoteText = `Please refer to a statement from your current firm to complete this process.`;

const TransferInstructions = ({
  formikBag: { values, errors, touched, setFieldValue, setFieldTouched },
  location
}: TransferInstructionsProps) => {
  useScrollTop(location);

  const fieldClasses = cx(formGroupStyles["form-group"]);
  const summaryDivClass = css`
    margin-left: 27px;
  `;

  const wrappingText = css`
    word-wrap: break-word;
    overflow-wrap: break-word;
  `;

  const widthClass = css`
    width: 350px;
  `;

  const widthPercentageClass = css`
    width: 400px;
  `;

  const clearStateWhenSwitching = () => {
    setFieldValue(FieldNameEnum.transferDollarAmount, "");
    setFieldValue(FieldNameEnum.transferDollarEstimate, "");
    setFieldValue(FieldNameEnum.transferPercent, "");
    setFieldTouched(FieldNameEnum.transferDollarAmount, false);
    setFieldTouched(FieldNameEnum.transferDollarEstimate, false);
    setFieldTouched(FieldNameEnum.transferPercent, false);
  };

  const potentialErrorFields: FieldNameEnum[] = [
    FieldNameEnum.transferDollarAmount,
    FieldNameEnum.transferDollarEstimate,
    FieldNameEnum.transferPercent
  ];

  // Checks all errors in the entire applicaiton and compares them with the fields on the current page
  const pageValid = potentialErrorFields.every(field => !(field in errors));

  const options: ComplicateOption[] = [
    {
      optionDisplayText: "All of the account",
      optionId: TransferInstructionsEnum.All,
      onOptionSelected: () => {
        clearStateWhenSwitching();
      },
      selectedRenderContent: () => (
        <div className={cx(formGroupStyles.contentPadding, widthClass)}>
          <Field
            label="Approximate value:"
            name={FieldNameEnum.transferDollarEstimate}
            component={FieldTextMoney}
          />
        </div>
      )
    },
    {
      optionDisplayText: "Only a specific amount",
      optionId: TransferInstructionsEnum.Dollar,
      onOptionSelected: () => {
        clearStateWhenSwitching();
      },
      selectedRenderContent: () => (
        <div className={cx(formGroupStyles.contentPadding, widthClass)}>
          <Field
            name={FieldNameEnum.transferDollarAmount}
            component={FieldTextMoney}
          />
        </div>
      )
    },
    {
      optionDisplayText: "Only a percentage of the account",
      optionId: TransferInstructionsEnum.Percent,
      onOptionSelected: () => {
        clearStateWhenSwitching();
      },
      selectedRenderContent: () => (
        <div
          className={cx(formGroupStyles.contentPadding, widthPercentageClass)}
        >
          <Field
            name={FieldNameEnum.transferPercent}
            component={FieldTextPercentage}
          />
        </div>
      )
    }
  ];

  return (
    <div className={ClassName.transferInstructions}>
      <Breadcrumbs currentStep={1} />
      <div className={bootstrapStyles.row}>
        <div
          className={cx(
            bootstrapStyles["col-md-12"],
            formGroupStyles.contentPadding
          )}
        >
          <StepHeader stepHeader={stepHeaderText} stepNote={stepNoteText} />
          <p>All fields required unless otherwise noted.</p>
        </div>
      </div>

      {/* Summary of information provided on prior page */}
      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-md-12"]}>
          <div className={fieldClasses}>
            <p className={formGroupStyles["label-like-text"]}>
              Transfer Account From
            </p>
            <div className={summaryDivClass} data-dd-privacy="mask">
              <div
                id="currentFirm"
                className={cx(wrappingText, formGroupStyles.bold)}
              >
                {values.currentFirm}
              </div>
              <div
                id="currentAccountType"
                className={cx(wrappingText, formGroupStyles.bold)}
              >
                {values.currentAccountType}
              </div>
              <div
                id="currentAccount"
                className={cx(wrappingText, formGroupStyles.bold)}
              >
                {values.currentAccount}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={cx(bootstrapStyles.row)}>
        <div className={bootstrapStyles["col-md-12"]}>
          <Field
            label={() => (
              <div className={wrappingText}>
                <span>
                  {`How much of your ${values.currentFirm} account would you like
                to liquidate and transfer to American Century? `}
                </span>
                <PfTooltip tooltipText="This liquidation and transfer is not a taxable event." />
                {values.rothPortion === RothPortionEnum.some && (
                  <div
                    style={{
                      marginTop: "24px",
                      fontStyle: "italic",
                      marginLeft: "25px"
                    }}
                  >
                    <span id="rothDisclaimerForSome">
                      For rollovers that represent less than 100% of the account
                      balance, we assume the distributions will be paid
                      proportionally from each eligible money source. Specific
                      rules for your retirement plan vary by provider.
                    </span>
                  </div>
                )}
              </div>
            )}
            options={options}
            component={FieldRadioList}
          />
        </div>
      </div>

      <div className={bootstrapStyles.row}>
        <div className={bootstrapStyles["col-md-12"]}>
          <hr />
        </div>
      </div>

      <div className={bootstrapStyles.row}>
        <div className={cx(bootstrapStyles["col-md-12"])}>
          <GeneralButton
            id="back"
            text="back"
            buttonType="secondary"
            onClick={() => {
              navigate("/transferDetails");
            }}
          />
          <div
            className={css`
              float: right;
            `}
          >
            <GeneralButton
              id="continue"
              text="continue"
              onClick={() => {
                navigate("/personalInformation");
              }}
              buttonType="primary"
              disabled={!pageValid}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferInstructions;
